// src/redux/user.js
import { cfAPI } from "../api";
import { createRequestHandler } from "../utils/requestHandlers";
import { toast } from "react-toastify";

// Action Types
export const USER_REQUEST = "USER_REQUEST";
export const USER_ERROR = "USER_ERROR";
export const USER_LOGIN_SUCCESS = "USER_LOGIN_SUCCESS";
export const USER_LOGOUT = "USER_LOGOUT";
export const USER_REFRESH_TOKEN = "USER_REFRESH_TOKEN";
export const USER_PROFILE_EDIT = "USER_PROFILE_EDIT";
export const USER_SIGN_UP_REQUEST = "USER_SIGN_UP_REQUEST";
export const USER_SIGN_UP_SUCCESS = "USER_SIGN_UP_SUCCESS";
export const USER_SIGN_UP_FAIL = "USER_SIGN_UP_FAIL";
export const USER_FORGOT_PASSWORD_REQUEST = "USER_FORGOT_PASSWORD_REQUEST";
export const USER_FORGOT_PASSWORD_FAIL = "USER_FORGOT_PASSWORD_FAIL";
export const RESET_STATE = "RESET_STATE";

const BaseRoute = "/userapi/api/v1";

// Reusable action types object for consistency
const actionTypes = {
  login: {
    request: USER_REQUEST,
    success: USER_LOGIN_SUCCESS,
    error: USER_ERROR,
  },
  profileEdit: {
    request: USER_REQUEST,
    success: USER_PROFILE_EDIT,
    error: USER_ERROR,
  },
  signUp: {
    request: USER_SIGN_UP_REQUEST,
    success: USER_SIGN_UP_SUCCESS,
    error: USER_SIGN_UP_FAIL,
  },
  forgotPassword: {
    request: USER_REQUEST,
    success: USER_FORGOT_PASSWORD_REQUEST,
    error: USER_ERROR,
  },
  forgotPasswordFail: {
    request: USER_REQUEST,
    success: null,
    error: USER_FORGOT_PASSWORD_FAIL,
  },
  resetPassword: {
    request: USER_REQUEST,
    success: USER_FORGOT_PASSWORD_REQUEST,
    error: USER_ERROR,
  },
};

// Actions using the new handlers
export const UserRefreshToken = (accessToken) => (dispatch) => {
  dispatch({
    type: USER_REFRESH_TOKEN,
    payload: accessToken,
  });
};

export const UserLogIn = (formValues) => async (dispatch) => {
  try {
    dispatch({
      type: actionTypes.login.request,
    });

    const newFormValues = {
      usernameOrEmail: formValues.email,
      password: formValues.password,
    };

    const loginResponse = await cfAPI.post(
      `${BaseRoute}/auth/signin`,
      newFormValues
    );

    const userResponse = await cfAPI.get(
      `${BaseRoute}/user/${loginResponse.headers.email}`,
      {
        headers: { Authorization: `Bearer ${loginResponse.headers.token}` },
      }
    );

    dispatch({
      type: actionTypes.login.success,
      payload: {
        token: loginResponse.headers.token,
        refreshToken: loginResponse.headers.refreshToken,
        user: userResponse.data,
      },
    });
  } catch (error) {
    // 👇 The correct way to handle the error
    const errorMessage =
      error.response && error.response.data.message
        ? error.response.data.message
        : error.message;

    dispatch({
      type: USER_ERROR,
      payload: errorMessage,
    });
  }
};

export const UserProfileUpdate = (formValues) => async (dispatch) => {
  await createRequestHandler(
    dispatch,
    () => cfAPI.patch(`${BaseRoute}/user/${formValues.email}`, formValues),
    actionTypes.profileEdit,
    "Profile Updated"
  );
};

export const UserSignUp = (formValues) => async (dispatch) => {
  await createRequestHandler(
    dispatch,
    () => cfAPI.post(`${BaseRoute}/auth/signup`, formValues),
    actionTypes.signUp,
    "Sign up Successful"
  );
};

export const UserLogout = () => (dispatch) => {
  localStorage.removeItem("persist:293939fk");
  sessionStorage.removeItem("persist:293939fk");
  toast.success("Logged out successfully");
  dispatch({ type: USER_LOGOUT });
};

export const PasswordReset = (formValues) => async (dispatch) => {
  await createRequestHandler(
    dispatch,
    () => cfAPI.post(`${BaseRoute}/auth/initiate-password-reset`, formValues),
    actionTypes.forgotPassword,
    "Password reset initiated"
  );
};

export const PasswordResetToken = (formValues) => async (dispatch) => {
  await createRequestHandler(
    dispatch,
    () => cfAPI.patch(`${BaseRoute}/auth/reset-password`, formValues),
    actionTypes.resetPassword,
    "Password has been reset"
  );
};

export const PassResetState = () => ({
  type: RESET_STATE,
});

const initialState = {
  loading: false,
  userInfo: null,
  error: null,
  passwordResetStatus: "failure", // 👈 Removed navOpen
};

export const UserReducer = (state = initialState, action) => {
  switch (action.type) {
    case USER_REQUEST:
    case actionTypes.signUp.request:
      return { ...state, loading: true, error: null };

    case USER_LOGIN_SUCCESS:
      return { ...state, loading: false, userInfo: action.payload };

    case USER_PROFILE_EDIT:
      return {
        ...state,
        loading: false,
        userInfo: { ...state.userInfo, user: action.payload },
      };

    case USER_SIGN_UP_SUCCESS:
      return { ...state, loading: false, error: null };

    case USER_LOGOUT:
      return { ...initialState, userInfo: null };

    case USER_ERROR:
    case actionTypes.signUp.error:
    case actionTypes.forgotPasswordFail.error:
      return { ...state, loading: false, error: action.payload };

    case USER_REFRESH_TOKEN:
      return {
        ...state,
        userInfo: { ...state.userInfo, token: action.payload },
      };

    case USER_FORGOT_PASSWORD_REQUEST:
      return { ...state, loading: false, passwordResetStatus: action.payload };

    case RESET_STATE:
      return { ...state, passwordResetStatus: "failure" };

    default:
      return state;
  }
};
